import React from 'react';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

interface ProcessingScreenProps {
  title?: string;
  progress: number;
  processed: number;
  total: number;
  succeeded?: number;
  failed?: number;
  pending?: number;
}

const ProcessingScreen: React.FC<ProcessingScreenProps> = ({
  title = 'Finding owner emails...',
  progress,
  processed,
  total,
  succeeded = 0,
  failed = 0,
  pending,
}) => {
  const safeTotal = total > 0 ? total : 0;
  const percent = safeTotal > 0 ? Math.round((processed / safeTotal) * 100) : progress;
  const pendingCount = pending ?? Math.max(0, safeTotal - processed);

  return (
    <div className="pb-1 pt-2">
      <div className="flex items-center gap-3 mb-4">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
        <span className="text-base font-medium text-gray-900">
          {title} {safeTotal > 0 ? `(${safeTotal})` : ''}
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 mb-1.5">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mb-4">
        <span>{processed} / {safeTotal || '…'}</span>
        <span>{percent}%</span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 mb-4">
        <div className="flex items-center gap-1.5 rounded-lg px-3 py-2 bg-emerald-50">
          <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-500" />
          <div>
            <div className="text-lg font-extrabold leading-none text-emerald-600">{succeeded}</div>
            <div className="text-[10px] text-gray-400">found</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg px-3 py-2 bg-gray-50">
          <XCircle className="w-4 h-4 flex-shrink-0 text-gray-400" />
          <div>
            <div className="text-lg font-extrabold leading-none text-gray-500">{failed}</div>
            <div className="text-[10px] text-gray-400">no email</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg px-3 py-2 bg-gray-50 col-span-2 sm:col-span-1">
          <Clock className="w-4 h-4 flex-shrink-0 text-gray-400" />
          <div>
            <div className="text-lg font-extrabold leading-none text-gray-500">{pendingCount}</div>
            <div className="text-[10px] text-gray-400">pending</div>
          </div>
        </div>
      </div>

      <p className="text-center text-sm text-gray-500 leading-snug px-1">
        Safe to close — enrichment continues in background.
      </p>
    </div>
  );
};

export default ProcessingScreen;
